import axios from 'axios';
import { IResultState } from './resultInterfaces';
import { IAppContext } from '../context/DefaultAppState';
import IDoctorSpecialty from '../interfaces/IDoctorSpecialty';
import IFacilitySpecialty from '../interfaces/IFacilitySpecialty';
import { getCurrentPosition } from './util';


// -------------------------
// TypeScript fix for custom window property
// -------------------------
declare global {
  interface Window {
    currentPlanUrl?: string;
  }
}

// Store current site globally
export const setGlobalPlanUrl = (planUrl: string) => {
  window.currentPlanUrl = planUrl;
  console.log("🌐 currentPlanUrl set globally:", window.currentPlanUrl);
};

// Dentist icons — use relative paths for <base> compatibility
export const dentistIconMap: Record<string, string> = {
  "localhost:3000": "icons/dentist-ageright.png",
  "agerightadvantage.com": "icons/dentist-ageright.png",
  "alignseniorcare.com": "icons/dentist-align.png",
  "keycareadvantage.com": "icons/dentist-keycare.png",
  "lifeworksadvantage.com": "icons/dentist-lifeworks.png",
  "nhcadvantageplan.com": "icons/dentist-nhc.png",
  "perennialadvantage.com": "icons/dentist-perennial.png",
  "procareadvantageplan.com": "icons/dentist-procare.png",
  "pruitthealthpremier.com": "icons/dentist-ageright.png",
};

// -------------------------
// Fetch plan data and set theme
// -------------------------
export const getPlanData = async (appContext: IAppContext) => {
  let planUrl = window.location.host.trim();

  // Dev → Prod mapping
  const devMap: Record<string, string> = {
    "agerightdev.wpengine.com": "agerightadvantage.com",
    "agerighttest.wpengine.com": "agerightadvantage.com",
    "alignscrdev.wpengine.com": "alignseniorcare.com",
    "alignscstage.wpengine.com": "alignseniorcare.com",
    "keycaredev.wpengine.com": "keycareadvantage.com",
    "keycarestage.wpengine.com": "keycareadvantage.com",
    "lifeworkdev.wpengine.com": "lifeworksadvantage.com",
    "lifeworksstage.wpengine.com": "lifeworksadvantage.com",
    "nhcadvdev.wpengine.com": "nhcadvantageplan.com",
    "nhcadvstage.wpengine.com": "nhcadvantageplan.com",
    "perennialaddev.wpengine.com": "perennialadvantage.com",
    "perennialadstg.wpengine.com": "perennialadvantage.com",
    "procareadvdev.wpengine.com": "procareadvantageplan.com",
    "procareadvstg.wpengine.com": "procareadvantageplan.com",
    "pruittpremdev.wpengine.com": "pruitthealthpremier.com",
    "pruittpremstg.wpengine.com": "pruitthealthpremier.com",
    "localhost:3001": "lifeworksadvantage.com",
     "localhost:3000": "procareadvantageplan.com",
  };

  if (devMap[planUrl]) planUrl = devMap[planUrl];

  console.log("👉 Final planUrl being used:", planUrl);
  setGlobalPlanUrl(planUrl);

  try {
    const { data } = await axios.get(`/Plans`);
    let plans = data?.filter((p: any) => p.planSite?.toLowerCase() === planUrl?.toLowerCase());

    // Lagniappe fallback (optional, can remove if not needed)
    if ((!plans || plans.length === 0) && planUrl === "lagniappeadvantage.com") {
      plans = [
        {
          id: -999,
          name: "Lagniappe Advantage PPO-I-SNP",
          shortPlanName: "Lagniappe Advantage",
          planAbbreviation: "LAP",
          state: "Louisiana",
          stateAbbreviation: "LA",
          planType: "PPO-I-SNP",
          planSite: "lagniappeadvantage.com",
          contractId: "H6566",
          styleTheme: "lagniappe-theme",
        },
      ];
    }

    if (!plans || plans.length === 0) return;

    const plan = plans[0];

    document.documentElement.setAttribute("theme", plan.styleTheme?.toLowerCase());
    appContext.updatePlanName(plan.name);
    appContext.updateShortPlanName(plan.shortPlanName);
    appContext.updatePlansData(plans);
  } catch (err) {
    console.error("❌ Error fetching plans:", err);
  }
};



const getDoctorSpecialties = async (appContext: IAppContext) => {
  try {
    const res = await axios.get(`/DoctorSpecialties`);
    const specialties: IDoctorSpecialty[] = res.data;
    appContext.updateDoctorSpecialties(specialties);
  } catch (err) {
    console.error(err);
  }
};

const getFacilitySpecialties = async (appContext: IAppContext) => {
  try {
    const res = await axios.get(`/FacilitySpecialties`);
    const specialties: IFacilitySpecialty[] = res.data;
    appContext.updateFacilitySpecialties(specialties);
  } catch (err) {
    console.error(err);
  }
};

const getPharmacySpecialties = async (appContext: IAppContext) => {
  try {
    const res = await axios.get(`/PharmacySpecialties`);
    const specialties: string[] = res.data;
    appContext.updatePharmacySpecialties(specialties);
  } catch (err) {
    console.error(err);
  }
};

// const getStates = async (appContext: IAppContext) => {
//   const emptyStateOption: IResultState = {
//     state: 'Pick your state',
//     abbreviation: 'default',
//     code: 'default'
//   };
//
//   try {
//     const { data } = await axios.get(`/States`);
//     const states = data;
//     console.log('STATES REQ: ', states);
//     states.unshift(emptyStateOption);
//     appContext.updateStateList(states);
//   } catch (err) {
//     console.error(err);
//   }
// };

const getCacheTime = async (appContext: IAppContext) => {
  try {
    const { data } = await axios.get(`/CacheTime`);
    appContext.updateCacheTime(data);
  } catch (err) {
    console.error(err);
  }
};

const getCurrentOrigin = async (appContext: IAppContext) => {
  let browserPosition = false;
  try {
    let currentLocation = { lat: 0, lng: 0 };
    /* const res: any = await getCurrentPosition();
    const { latitude, longitude } = res.coords;
    currentLocation.lat = latitude;
    currentLocation.lng = longitude;
    
    browserPosition = true; */
    //appContext.updateOrigin(currentLocation);
  } catch (err) {
    console.error(err);
  }
  if (!browserPosition) {
    try {
      let currentLocation = { lat: 0, lng: 0 };
      //const address = `${appContext.planState}`.split(' ').join('+');
      //console.log(address);
      // const geocode = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
      // //planLocation = geocode.data.results[0]?.geometry?.location;

      // const { latitude, longitude } = geocode.data.results[0]?.geometry?.location;;
      // currentLocation.lat = latitude;
      // currentLocation.lng = longitude;
      // console.log('Lat: ', latitude);
      // console.log('Lng: ', longitude);
      //appContext.updateOrigin(currentLocation);
    } catch (err) {
      //console.log(err);
    }
  };
}

const initializeApp = async (appContext: IAppContext) => {
  await getPlanData(appContext);
  // getTheme(appContext);
  await getCacheTime(appContext);
  // await getStates(appContext);
  await getDoctorSpecialties(appContext);
  await getFacilitySpecialties(appContext);
  await getPharmacySpecialties(appContext);
  //await getCurrentOrigin(appContext);
  // TODO: Add Loading Complete Update Here
};

export default initializeApp;
 